import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trash2, RotateCcw, AlertTriangle, CheckCircle, XCircle, Database } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { resetAllData } from '../../utils/resetData';

const ResetDataPage = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  // Redirect non-super-admins
  useEffect(() => {
    if (!isSuperAdmin()) {
      navigate('/dashboard');
    }
  }, [isSuperAdmin, navigate]);

  const handleReset = async (resetType) => {
    setLoading(true);
    setError('');
    setResults(null);

    try {
      let result;
      
      switch (resetType) {
        case 'stats':
          result = await resetAllData.resetPlayerStats();
          break;
        case 'leagues':
          result = await resetAllData.deleteAllLeagues();
          break;
        case 'fixtures':
          result = await resetAllData.deleteAllFixtures();
          break;
        case 'all':
          result = await resetAllData.prepareForLive();
          break;
        default:
          throw new Error('Invalid reset type');
      }

      setResults(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetOptions = [
    {
      id: 'stats',
      title: 'Reset Player Stats',
      description: 'Reset all player statistics to zero',
      icon: RotateCcw,
      color: '#F59E0B',
      bgColor: '#FEF3C7',
      borderColor: '#FCD34D'
    },
    {
      id: 'leagues',
      title: 'Delete All Leagues',
      description: 'Remove all leagues from the system',
      icon: Trash2,
      color: '#DC2626',
      bgColor: '#FEF2F2',
      borderColor: '#FECACA'
    },
    {
      id: 'fixtures',
      title: 'Delete All Fixtures',
      description: 'Remove all fixtures and matches',
      icon: Database,
      color: '#7C3AED',
      bgColor: '#F3E8FF',
      borderColor: '#DDD6FE'
    },
    {
      id: 'all',
      title: 'Prepare for Live',
      description: 'Complete reset: stats + leagues + fixtures',
      icon: AlertTriangle,
      color: '#DC2626',
      bgColor: '#FEF2F2',
      borderColor: '#FECACA'
    }
  ];

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)',
      padding: '2rem'
    }}>
      <div style={{ maxWidth: '100%', margin: '0 auto', padding: '0 2rem' }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ marginBottom: '2rem' }}
        >
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '1rem',
            marginBottom: '1rem'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '1rem',
              background: 'linear-gradient(135deg, #DC2626 0%, #EF4444 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)'
            }}>
              <AlertTriangle size={32} style={{ color: 'white' }} />
            </div>
            <div>
              <h1 style={{ 
                fontSize: '2.5rem', 
                fontWeight: 'bold', 
                color: '#1F2937',
                margin: 0
              }}>
                Data Reset Center
              </h1>
              <p style={{ 
                fontSize: '1.125rem', 
                color: '#6B7280',
                margin: '0.5rem 0 0 0'
              }}>
                Reset player stats and prepare for live events
              </p>
            </div>
          </div>
        </motion.div>

        {/* Warning Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{
            background: 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)',
            border: '2px solid #FECACA',
            borderRadius: '1rem',
            padding: '1.5rem',
            marginBottom: '2rem'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <AlertTriangle size={24} style={{ color: '#DC2626' }} />
            <div>
              <h3 style={{ 
                fontSize: '1.25rem', 
                fontWeight: 'bold', 
                color: '#DC2626',
                margin: 0
              }}>
                ⚠️ Warning: Destructive Operations
              </h3>
              <p style={{ 
                color: '#991B1B', 
                margin: '0.5rem 0 0 0',
                fontSize: '0.875rem'
              }}>
                These operations will permanently delete data. Make sure you have backups before proceeding.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Reset Options */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          {resetOptions.map((option, index) => {
            const Icon = option.icon;
            return (
              <motion.div
                key={option.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                style={{
                  background: 'white',
                  borderRadius: '1rem',
                  padding: '1.5rem',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #E5E7EB',
                  transition: 'all 0.2s ease'
                }}
                whileHover={{ scale: 1.02, y: -2 }}
              >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '1rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '0.75rem',
                    background: option.bgColor,
                    border: `2px solid ${option.borderColor}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Icon size={24} style={{ color: option.color }} />
                  </div>
                  <div>
                    <h3 style={{ 
                      fontSize: '1.25rem', 
                      fontWeight: 'bold', 
                      color: '#1F2937',
                      margin: 0
                    }}>
                      {option.title}
                    </h3>
                    <p style={{ 
                      fontSize: '0.875rem', 
                      color: '#6B7280',
                      margin: 0
                    }}>
                      {option.description}
                    </p>
                  </div>
                </div>
                
                <button
                  onClick={() => handleReset(option.id)}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1.5rem',
                    background: loading ? '#9CA3AF' : option.color,
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {loading ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      >
                        <RotateCcw size={16} />
                      </motion.div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <Icon size={16} />
                      Execute Reset
                    </>
                  )}
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* Results */}
        {results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              background: results.success ? '#F0FDF4' : '#FEF2F2',
              border: `1px solid ${results.success ? '#BBF7D0' : '#FECACA'}`,
              borderRadius: '1rem',
              padding: '1.5rem',
              marginBottom: '1rem'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              {results.success ? (
                <CheckCircle size={24} style={{ color: '#166534' }} />
              ) : (
                <XCircle size={24} style={{ color: '#DC2626' }} />
              )}
              <div>
                <h3 style={{ 
                  fontSize: '1.125rem', 
                  fontWeight: 'bold', 
                  color: results.success ? '#166534' : '#DC2626',
                  margin: 0
                }}>
                  {results.success ? '✅ Success!' : '❌ Error'}
                </h3>
                <p style={{ 
                  color: results.success ? '#166534' : '#DC2626',
                  margin: '0.5rem 0 0 0'
                }}>
                  {results.message}
                </p>
                {results.details && (
                  <div style={{ marginTop: '1rem' }}>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', margin: '0 0 0.5rem 0' }}>
                      Details:
                    </h4>
                    {Object.entries(results.details).map(([key, value]) => (
                      <div key={key} style={{ fontSize: '0.75rem', color: '#6B7280', marginBottom: '0.25rem' }}>
                        <strong>{key}:</strong> {value}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              color: '#DC2626',
              padding: '1rem',
              borderRadius: '0.5rem'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <XCircle size={20} />
              <span>{error}</span>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ResetDataPage;
